/* eslint-disable @typescript-eslint/no-explicit-any */

import { submitFormJson } from 'widgets/toolbox/ajax';
import { isSignedIn, isMember } from 'core/utils';
import sessionStorageWrapper from 'core/toolbox/sessionStorageWrapper';

type TActionData = { [x: string]: string | number | boolean };

export type TPageLoadAction = {
    type: string;
    data: TActionData;
};

type TWrappedActionData = TActionData & {
    originalAction?: TPageLoadAction;
};

const SESSION_STORAGE_KEY = 'pageLoadAction';

export const PAGE_LOAD_ACTION_TYPES = {
    WISHLIST_QUICK_VIEW: 'wishlistQuickView',
    WISHLIST_ADD_TO_CART: 'wishlistAddToCart',
    PDP_ADD_TO_CART: 'pdpAddToCart',
    STORE_AVAILABILITY: 'storeAvailability',
    AVAILABILITY_REMINDER: 'availabilityReminder',
    ENSURE_LOYALTY_ENROLLMENT: 'ensureLoyaltyEnrollment'
};

/**
 * @description Base PageLoadActionProcessor implementation
 * @param Widget Base widget for extending
 * @returns Storage actions mixin widget
 */
function PageLoadActionProcessorClassCreator(Widget: import('widgets/Widget').TWidget) {
    class PageLoadActionProcessor extends Widget {
        prefs() {
            return {
                showQuickViewButton: 'showQuickViewBtn',
                isLoyaltyEnrollmentRequired: false,
                addToCartUrl: '',
                enrollLoyaltyModalUrl: '',
                enrollLoyaltyModalTitle: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.eventBus().on('addPageLoadAction', 'addPageLoadAction');

            this.processPageLoadAction();
        }

        /**
         * @description Store page load action in session storage
         * @param pageLoadAction Page load action object
         */
        addPageLoadAction(pageLoadAction: TPageLoadAction) {
            if (pageLoadAction) {
                sessionStorageWrapper.setItem(SESSION_STORAGE_KEY, JSON.stringify(pageLoadAction));
            }
        }

        /**
         * @description Handles wishlist quick view action
         * @param productTileID Product tile ID
         */
        wishlistQuickViewAction(productTileID: string) {
            return (
                this.getByIdDeep(productTileID, async (productTile: any) => {
                    const showQuickViewButton = productTile.ref(this.prefs().showQuickViewButton);

                    return productTile.editProduct(showQuickViewButton);
                }) || Promise.resolve(null)
            );
        }

        /**
         * @description Handles add to cart action
         * @param productTileID Product tile ID
         * @param data Action data
         */
        addToCartAction(productTileID: string, data: TActionData) {
            return (
                this.getByIdDeep(productTileID, async (productTile: any) => {
                    const response = await submitFormJson(this.prefs().addToCartUrl, data);

                    productTile.postAddProduct(response);

                    return response;
                }) || Promise.resolve(null)
            );
        }

        /**
         * @description Opens store availability drawer with the provided data
         * @param data Action data
         */
        storeAvailabilityAction(data: TActionData) {
            setTimeout(() => {
                const { submitEvent, eventData } = data;

                if (submitEvent) {
                    this.eventBus().emit(String(submitEvent), eventData);
                }
            }, 0);
        }

        /**
         * @description Ensures loyalty enrollment with the provided data
         * Handles the original action if the user is already a member, otherwise shows the loyalty enrollment modal
         * @param data Action data
         */
        ensureLoyaltyEnrollmentAction(data: TWrappedActionData) {
            const originalPageLoadAction = data.originalAction as TPageLoadAction;
            const prefs = this.prefs();

            if (isMember(this.prefs().isLoyaltyEnrollmentRequired)) {
                this.handleAction(originalPageLoadAction);
            } else {
                setTimeout(() => {
                    this.eventBus().emit('enrollLoyaltyToBuyModal.show', {
                        url: prefs.enrollLoyaltyModalUrl,
                        headerText: prefs.enrollLoyaltyModalTitle,
                        wrapperClasses: 'm-sign_in_to_buy',
                        pageLoadAction: originalPageLoadAction
                    });
                }, 0);
            }
        }

        /**
         * @description Opens availability reminder drawer with the provided data
         * @param data Action data
         */
        availabilityReminderAction(data: TActionData) {
            setTimeout(() => {
                this.eventBus().emit('drawershow', data);
            }, 0);
        }

        /**
         * @description Handles page load action based on the action type
         * @param pageLoadAction Page load action object
         */
        handleAction(pageLoadAction: TPageLoadAction) {
            const { type, data } = pageLoadAction;

            switch (type) {
                case PAGE_LOAD_ACTION_TYPES.WISHLIST_QUICK_VIEW:
                    this.wishlistQuickViewAction(String(data.pid));
                    break;
                case PAGE_LOAD_ACTION_TYPES.WISHLIST_ADD_TO_CART:
                    this.addToCartAction(String(data.pid), data);
                    break;
                case PAGE_LOAD_ACTION_TYPES.PDP_ADD_TO_CART:
                    this.addToCartAction('productDetail', data);
                    break;
                case PAGE_LOAD_ACTION_TYPES.STORE_AVAILABILITY:
                    this.storeAvailabilityAction(data);
                    break;
                case PAGE_LOAD_ACTION_TYPES.AVAILABILITY_REMINDER:
                    this.availabilityReminderAction(data);
                    break;
                case PAGE_LOAD_ACTION_TYPES.ENSURE_LOYALTY_ENROLLMENT:
                    this.ensureLoyaltyEnrollmentAction(data);
                    break;
                default:
                    break;
            }
        }

        /**
         * @description Process page load action from session storage
         */
        processPageLoadAction() {
            const pageLoadActionJSON = sessionStorageWrapper.getItem(SESSION_STORAGE_KEY);

            if (pageLoadActionJSON && isSignedIn()) {
                const pageLoadAction = JSON.parse(pageLoadActionJSON) as TPageLoadAction;

                this.handleAction(pageLoadAction);
            }

            sessionStorageWrapper.removeItem(SESSION_STORAGE_KEY);
        }
    }

    return PageLoadActionProcessor;
}

export type TPageLoadActionProcessor = ReturnType<typeof PageLoadActionProcessorClassCreator>;

export type TPageLoadActionProcessorInstance = InstanceType<TPageLoadActionProcessor>;

export default PageLoadActionProcessorClassCreator;
